import { inject, Injectable, signal } from '@angular/core';
import { BaseService } from '@fluxonos/features/shared/services/base-service';
import { environment } from '@fluxonos/environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { FluxonosUtils, TokenDto, UserSignInDto } from '@fluxonos/fluxonos-dto';
import { instanceToPlain } from 'class-transformer';
import { UserService } from '@fluxonos/features/user/services/user.service';
import { PermissionService } from '@fluxonos/features/user/services/permission.service';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {
  private readonly _httpClient: HttpClient = inject(HttpClient);
  private readonly _userService: UserService = inject(UserService);
  private readonly _permissionService: PermissionService = inject(PermissionService);

  private readonly _apiBase = environment.features.userModule.user.auth;

  private readonly _storageAuthTokenName = 'USER_AUTH_TOKEN';
  private readonly _token = signal<TokenDto | null>(null);
  private readonly _token$ = toObservable(this._token);

  public initialize(): Observable<boolean> {
    const tokenStr = localStorage.getItem(this._storageAuthTokenName);
    if (!tokenStr) {
      this._token.set(null);
      return of(true);
    }
    const token = FluxonosUtils.transformToDto(TokenDto, JSON.parse(tokenStr));
    this._token.set(token);

    return this._userService.get().pipe(
      switchMap(() => this._permissionService.list()),
      map(() => true)
    );
  }

  public signIn(data: UserSignInDto): Observable<TokenDto> {
    const endpoint = this.getEndpoint(this._apiBase.signIn);

    return this._httpClient
      .request<TokenDto>(endpoint.method, endpoint.url, {
        withCredentials: true,
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        body: data
      })
      .pipe(
        map((res) => FluxonosUtils.transformToDto(TokenDto, res)),
        switchMap((token: TokenDto) => {
          // Save to local storage
          localStorage.setItem(
            this._storageAuthTokenName,
            JSON.stringify(instanceToPlain(token, { excludeExtraneousValues: true }))
          );
          return of(true).pipe(
            tap(() => this._token.set(token)),
            switchMap(() => this._userService.get()),
            switchMap(() => this._permissionService.list()),
            map(() => token)
          );
        })
      );
  }

  public signOut(): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.signOut);

    return this._httpClient
      .request<boolean>(endpoint.method, endpoint.url, {
        withCredentials: true,
        headers: {
          'Content-Type': endpoint.header['contentType']
        }
      })
      .pipe(
        map((res: boolean) => {
          if (res) {
            this.cleanUp();
          }
          return res;
        })
      );
  }

  public refreshToken(
    hideLoadingSpinner: boolean = false,
    hideErrorMessage: boolean = false,
    withError: boolean = false
  ): Observable<TokenDto | boolean> {
    if (!this.token?.accessToken) {
      return throwError(() => 'Auth-Token does not exists!');
    }

    const endpoint = this.getEndpoint(this._apiBase.refreshToken);
    return this._httpClient
      .request<any>(endpoint.method, endpoint.url, {
        withCredentials: true,
        headers: {
          'Content-Type': endpoint.header['contentType'],
          hideLoadingSpinner: hideLoadingSpinner ? '1' : '0',
          hideErrorMessage: hideErrorMessage ? '1' : '0',
          skipAuth: '1'
        },
        body: {
          accessToken: this.token?.accessToken
        }
      })
      .pipe(
        map((res) => FluxonosUtils.transformToDto(TokenDto, res)),
        switchMap((token: TokenDto) => {
          localStorage.setItem(
            this._storageAuthTokenName,
            JSON.stringify(instanceToPlain(token, { excludeExtraneousValues: true }))
          );

          this._token.update(() => token);
          return this._userService.get().pipe(map(() => token));
        }),
        catchError((err) => {
          this.cleanUp();
          if (withError) {
            return throwError(() => err);
          }
          return of(true);
        })
      );
  }

  public cleanUp(): void {
    localStorage.removeItem(this._storageAuthTokenName);
    this._token.set(null);

    this._userService.cleanUp();
    this._permissionService.cleanUp();
  }

  public get token(): Readonly<TokenDto | null> {
    return this._token();
  }

  public get token$(): Observable<Readonly<TokenDto | null>> {
    return this._token$;
  }
}
